import { useState } from "react";

import { Alert } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  useQuery,
  UseQueryResult,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";

import DwellingFeaturesProperty from "@/modules/dwelling/components/sheet/DwellingFeaturesProperty.tsx";

import Loader from "../../../common/components/Loader";
import DwellingDiagnostic from "../components/sheet/DwellingDiagnostic.tsx";
import DwellingFiles from "../components/sheet/DwellingFiles.tsx";
import DwellingFurtherInformation from "../components/sheet/DwellingFurtherInformation.tsx";
import DwellingInfo from "../components/sheet/DwellingInfo.tsx";
import DwellingInterventionHistory from "../components/sheet/DwellingInterventionHistory.tsx";
import DwellingLastInventory from "../components/sheet/DwellingLastInventory.tsx";
import { DataDwellingInfos } from "../interfaces";
import { IDwelling } from "../reducers";
import {
  getDwellingInfos,
  getDwellingLastServiceInventory,
  deleteDwellingFile,
} from "../services";

interface RouteParams {
  id: string;
}

const StateData = ({
  isLoading,
  isError,
  error,
  render,
  data,
  isContainer,
}: UseQueryResult<any, Error> & {
  isContainer?: boolean;
  render: (data: any) => JSX.Element;
}) => {
  if (isError)
    return (
      <Alert variant="danger">
        Erreur chargement des données : {error?.message}
      </Alert>
    );
  if (isLoading)
    return (
      <div {...(isContainer && { className: "bg-container" })}>
        <Loader />
      </div>
    );
  return render(data);
};

const DwellingContainer = () => {
  const { id } = useParams<RouteParams>();
  const queryClient = useQueryClient();
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);

  const queryDwellingInfos = useQuery<IDwelling, Error, DataDwellingInfos>({
    queryKey: ["dwelling", "infos", id],
    queryFn: () => getDwellingInfos(id),
    enabled: !!id,
  });

  const queryDwellingLastServiceInventory = useQuery<IDwelling, Error>({
    queryKey: ["dwelling", "lastServiceInventory", id],
    queryFn: () => getDwellingLastServiceInventory(id),
    enabled: !!id,
  });

  // Mutation for deleting dwelling files
  const deleteDwellingFileMutation = useMutation({
    mutationFn: ({
      dwellingId,
      fileId,
    }: {
      dwellingId: string;
      fileId: string;
    }) => deleteDwellingFile(dwellingId, fileId),
    onMutate: ({ fileId }) => {
      setDeletingFileId(fileId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dwelling", "infos", id] });
      setDeletingFileId(null);
      toast.success("Document supprimé avec succès");
    },
    onError: (error) => {
      console.error("Error deleting file:", error);
      toast.error("Erreur lors de la suppression du fichier.");
      setDeletingFileId(null);
    },
  });

  const onDeleteFile = (fileId: string) => {
    confirmDialog({
      message: "Êtes-vous sûr de vouloir supprimer ce document ?\n\nCette action est irréversible. Le document sera définitivement supprimé et ne pourra pas être récupéré.",
      header: "Confirmer la suppression",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary mx-2",
      acceptLabel: "Supprimer définitivement",
      rejectLabel: "Annuler",
      accept: () => {
        if (id && fileId) {
          deleteDwellingFileMutation.mutate({ dwellingId: id, fileId });
        }
      },
    });
  };

  return (
    <div className="dwelling d-flex flex-column gap-3">
      <ConfirmDialog />
      <StateData
        {...queryDwellingInfos}
        isContainer={true}
        render={(dataDwellingInfos: DataDwellingInfos) => (
          <DwellingInfo data={dataDwellingInfos} />
        )}
      />

      <StateData
        {...queryDwellingInfos}
        isContainer={true}
        render={(dataDwellingInfos: DataDwellingInfos) => (
          <div className="bg-container dwelling_property d-flex flex-column gap-4">
            <div className="d-flex justify-content-between">
              <span className="dwelling_title">Caractéristiques du bien</span>
            </div>
            <DwellingFeaturesProperty dwelling={dataDwellingInfos} />
          </div>
        )}
      />

      <StateData
        {...queryDwellingLastServiceInventory}
        isContainer={true}
        render={(lastServiceInventoryReport) =>
          lastServiceInventoryReport ? (
            <DwellingLastInventory
              dwellingId={id}
              report={lastServiceInventoryReport}
            />
          ) : (
            <></>
          )
        }
      />

      <StateData
        {...queryDwellingInfos}
        isContainer={true}
        render={(dataDwellingInfos: DataDwellingInfos) => (
          <DwellingDiagnostic dwelling={dataDwellingInfos} />
        )}
      />

      <DwellingInterventionHistory dwellingId={id} />

      <StateData
        {...queryDwellingInfos}
        isContainer={true}
        render={(dataDwellingInfos: DataDwellingInfos) => (
          <DwellingFurtherInformation data={dataDwellingInfos} />
        )}
      />

      <StateData
        {...queryDwellingInfos}
        isContainer={true}
        render={(dataDwellingInfos: DataDwellingInfos) => (
          <DwellingFiles
            dwelling={dataDwellingInfos}
            onDeleteFile={onDeleteFile}
            isDeleting={deleteDwellingFileMutation.isPending}
            deletingFileId={deletingFileId || undefined}
          />
        )}
      />
    </div>
  );
};

export default DwellingContainer;
